import { CombinedGraphQLErrors } from "@apollo/client/errors";
import * as ApolloLib from "apollo-angular";
import { Apollo } from "apollo-angular";
import { Subscription as RxSubscription } from "rxjs";

import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";

import { NotificationType } from "../../../apis/networkdashboard/graphql/graphql";
import { CimQueryImpactTypes, CimQueryStatuses } from "../../../apis/networkdashboard/static/custom-types";
import { CIM_SUBSCRIPTION } from "../../../apis/networkdashboard/static/graphql-queries/cim-notifications";
import { HealthService } from "../../../apis/stats/rest/services";
import { serviceHealth } from "../../../components/dashboard/service-health-signal/service-health-signal";
import { DialogAuthComponent } from "../../../components/dialogs/dialog-auth/dialog-auth";
import { openSelfServiceDialog } from "../../../components/dialogs/dialog-selfservice/dialog-selfservice.helpers";
import { SubscriptionDetailTab } from "../../../components/subscription-detail/page-tab/tab-types";
import { ApiHelper } from "../../../helpers/apihelper";
import { registerDirtyRefresher } from "../../../helpers/dirty-subscription-refresher";
import { SelfserviceDialogType } from "../../../helpers/self-service/models/types";
import { AuthService } from "../../../services/authservice";
import { MessageService } from "../../../services/messageservice";
import { phraseDetailPath } from "../subscription-detail-redirection";
import { MinimalSubscription } from "../types";

type DetailsVariables = { subscriptionId: string; accessCustomerId: string };

// prettier-ignore
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    template: "",
})
export abstract class GenericSubscription<
  R,
  S extends MinimalSubscription,
> implements OnDestroy, OnInit {
  protected api = inject(ApiHelper);
  protected apollo = inject(Apollo);
  protected auth = inject(AuthService);
  protected dialog = inject(MatDialog);
  protected route = inject(ActivatedRoute);
  protected msgsrv = inject(MessageService);
  private healths = inject(HealthService);

  readonly activeTab = input<SubscriptionDetailTab>();

  incident = output<string>();
  notification = output<string>();

  public impactSetting = "Never";
  protected activities = signal<NotificationType[]>([]);

  // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
  public dialogRef: MatDialogRef<MatDialog>; // TODO test

  public _subscriptionId = "";

  protected isLoading = computed(() => this.pageStatus() === this.isLoading)
  protected pageLoaded = computed(() => this.pageStatus() === this.pageLoaded)
  protected isNotFound = computed(() => this.pageStatus() === this.isNotFound)
  protected isNotAuthorized = computed(() => this.pageStatus() === this.isNotAuthorized)

  private pageStatus = signal(this.isLoading)

  protected abstract query: ApolloLib.Query<R, DetailsVariables>;

  public subscription = signal<S | undefined>(undefined);

  protected productTag = computed(() => this.subscription()?.product.tag ?? "");
  protected productType = computed(() => this.subscription()?.product.type ?? "");
  protected isDirty = computed(() => this.subscription()?.dirty ?? false);

  protected health = serviceHealth(this.subscription);
  protected reportedStatus = computed(() => this.health().status);

  private rxSubscriptions: RxSubscription[] = [];

  public router = inject(Router);

  constructor() {
    this._subscriptionId = this.route.snapshot.paramMap.get("subscriptionId") ?? "";
    registerDirtyRefresher(this.isDirty, () => this.querySubscription(this.query));
  }

  ngOnInit() {
    const initSubscription = this.auth.userLoaded.subscribe((loaded) => loaded ? this.refresh() : undefined);
    const refreshSubscription = this.msgsrv.selfServiceMessage$.subscribe((msg) => msg === "refresh" ? this.refresh() : undefined);
    this.rxSubscriptions.push(initSubscription);
    this.rxSubscriptions.push(refreshSubscription);
  }

  ngOnDestroy() {
    while (this.rxSubscriptions.length) {
      this.rxSubscriptions.pop()?.unsubscribe();
    }
  }

  /** Notifications **/
  fetchActivities() {
    const subscription = this.apollo
      .query<{ notifications: Array<NotificationType> }>({
        query: CIM_SUBSCRIPTION,
        fetchPolicy: "network-only",
        variables: {
          filter: {
            customerId: this.auth.viewingCustomer().customerId,
            accessCustomerId: this.auth.viewingCustomer().customerId,
            statuses: [CimQueryStatuses.OPEN, CimQueryStatuses.UPDATED],
            impacts: [
              CimQueryImpactTypes.DOWN,
              CimQueryImpactTypes.REDUCED_REDUNDANCY,
              CimQueryImpactTypes.RESILIENCE_LOSS,
            ],
            subscriptionIds: [this._subscriptionId],
          },
        },
      })
      .subscribe({
        next: (result) => {
          // TODO #206 Address technical debt Notifications/Messages/PlannedWorks
          if (result.data) {
            this.activities.set(result.data.notifications);
          }
        },
        // notifications are non-critical: a failed query should not take down the detail page
        error: () => this.activities.set([]),
      });
    this.rxSubscriptions.push(subscription);
  }

  /** Dialog and tab interactivity **/
  changeActiveTab(tabName: SubscriptionDetailTab) {
    const path = phraseDetailPath(this.productType(), this.productTag(), this._subscriptionId);
    path.push(tabName);
    this.router.navigate(path, {replaceUrl: true });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  openSelfServiceDialog(type: SelfserviceDialogType) {
    const roleCheckResult = this.auth.checkRoleAccess(this.productType(), "edit");
    if (!roleCheckResult.ok) {
      this.auth.roleEvent.emit(roleCheckResult);
      return;
    }

    if (!this.auth.selfServiceStarted()) {
      const ssAuthData = { data: { state: false, initialAction: type, selfserviceState: { handler: "generic-subscription" } }};
      this.dialog.open(DialogAuthComponent, ssAuthData);
    } else {
      const ssDialogData = { type, subscription: this.subscription() };

      // @ts-ignore FIXME this.subscription is not guaranteed to be of a type required by self-service dialog
      const dialogRef = openSelfServiceDialog<S>(this.dialog, ssDialogData);
      dialogRef.afterClosed().subscribe((event: string) => {
        this.msgsrv.setSelfServiceMessage(event);
      });
    }
  }

  /** Other */
  private refresh = async () => {
    this.querySubscription(this.query);
    this.fetchActivities();
  }

  private querySubscription(query: ApolloLib.Query<R, DetailsVariables>) {
    const subscription = query
      .watch({ variables: { subscriptionId: this._subscriptionId, accessCustomerId: this.auth.viewingCustomer().customerId }, fetchPolicy: "network-only" })
      .valueChanges.subscribe((result) => {
        if (result.dataState === "complete") {
          this.subscription.set(this.onQuerySuccess(result.data));
          this.pageStatus.set(this.pageLoaded);
        } else if (result.error) {
          const errorType = CombinedGraphQLErrors.is(result.error)
            ? result.error.errors[0]?.extensions?.["error_type"]
            : undefined;
          // Default to not-found so an unrecognized error never strands the page on the loading spinner.
          this.pageStatus.set(errorType === "not_authorized" ? this.isNotAuthorized : this.isNotFound);
        }
      });
    this.rxSubscriptions.push(subscription);
  }

  protected abstract onQuerySuccess(data: R): S;
}
